import type { PolygonWithHoles, Vec2, Vec3 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import type { BespokeGate, GateBlock, GatePassage } from '../../fortifications/bespokeGate';
import { layoutToWorld } from '../../../../core/coords';
import { difference, fromRing } from '../../../../geo/clip';
import { orientedFace, planPoint } from '../../solids';
import { finial, roofTrim, type FortressStyles } from './fortressParts';
import { archedBlock, panel } from './fortressBlocks';
import { vane } from './towerDetails';
import { VANE, rectRing } from './sintAntoniespoortPlan';
import {
  HELM,
  JAN_ROODENPOORT_FRAME,
  JAN_ROODEN_FOOT,
  JAN_ROODEN_PASSAGE,
  JAN_ROODEN_TOWER,
  JAN_ROODEN_WINDOWS,
} from './janRoodenpoortPlan';

/** A plan point (u along the passage toward the field, v to its left) in layout coordinates. */
export function janRoodenToLayout(p: Vec2): Vec2 {
  const { origin, heading } = JAN_ROODENPOORT_FRAME;
  const [c, s] = [Math.cos(heading), Math.sin(heading)];
  return [origin[0] + p[0] * c - p[1] * s, origin[1] + p[0] * s + p[1] * c];
}

/* The tower's corners counter-clockwise in plan, and the middles of the faces between them. */
function helmRing(): { corners: Vec2[]; gables: Vec2[] } {
  const { u, v } = JAN_ROODEN_TOWER;
  const corners: Vec2[] = [
    [u[0], v[0]],
    [u[1], v[0]],
    [u[1], v[1]],
    [u[0], v[1]],
  ];
  const gables = corners.map((c, k): Vec2 => {
    const d = corners[(k + 1) % 4]!;
    return [(c[0] + d[0]) / 2, (c[1] + d[1]) / 2];
  });
  return { corners, gables };
}

/* Outward from the spire's axis at a triangle's centroid, and upward, in the builder's frame. */
function outwardOf(points: readonly Vec3[]): Vec3 {
  const c = points.reduce((a, p): Vec3 => [a[0] + p[0], a[1] + p[1], a[2] + p[2]], [0, 0, 0]);
  return [c[0] / points.length, points.length, c[2] / points.length];
}

/**
 * The helm closes on the walls' top edges: a brick gable in each wall's plane and the spire's faces
 * from the gable tips and the corners to the apex. Left open below, where the tower's walls go on.
 */
function helm(builder: MeshBuilder, styles: FortressStyles, frame: Frame): void {
  const { eave } = JAN_ROODEN_TOWER;
  const { corners, gables } = helmRing();
  const at = (p: Vec2, y: number) => planPoint(p[0], y, p[1]);
  const apex = at([0, 0], HELM.apex);
  corners.forEach((c, k) => {
    const d = corners[(k + 1) % 4]!;
    const g = at(gables[k]!, HELM.gable);
    const wall = [at(c, eave), at(d, eave), g];
    const [mu, mv] = gables[k]!;
    orientedFace(builder, styles.wall, frame, wall, [mu, 0, -mv]);
    const next = at(gables[(k + 1) % 4]!, HELM.gable);
    for (const tri of [
      [g, at(d, eave), apex],
      [at(d, eave), next, apex],
    ]) {
      orientedFace(builder, styles.roof, frame, tri, outwardOf(tri));
    }
  });
}

function crown(builder: MeshBuilder, styles: FortressStyles, frame: Frame): void {
  helm(builder, styles, frame);
  finial(
    builder,
    styles.roof,
    frame,
    [0, 0],
    { base: HELM.apex - 0.3, top: HELM.finialTop },
    HELM.finialRadius
  );
  const { height, radius } = HELM.gableFinial;
  for (const g of helmRing().gables) {
    finial(
      builder,
      styles.roof,
      frame,
      g,
      { base: HELM.gable - 0.1, top: HELM.gable + height },
      radius
    );
  }
  const at = { centre: [0, 0] as Vec2, top: HELM.finialTop, azimuth: 0 };
  vane(builder, roofTrim(styles.roof), frame, at, VANE);
}

function windows(builder: MeshBuilder, style: VertexStyle, frame: Frame): void {
  const { u, v } = JAN_ROODEN_TOWER;
  const { width, sill, head, across } = JAN_ROODEN_WINDOWS;
  const faces = [
    { at: (s: number): Vec2 => [u[0], s], facing: Math.PI },
    { at: (s: number): Vec2 => [u[1], s], facing: 0 },
    { at: (s: number): Vec2 => [s, v[0]], facing: -Math.PI / 2 },
    { at: (s: number): Vec2 => [s, v[1]], facing: Math.PI / 2 },
  ];
  for (const face of faces)
    for (const s of across) {
      const spec = { sill, head, at: face.at(s), facing: face.facing, width, arch: true };
      panel(builder, style, frame, { ...spec, proud: 0.05, depth: 0.2 });
    }
}

/** The gate of 1538 on its plan (janRoodenpoortPlan.ts), in the frame of the layout's record. */
export function buildJanRoodenpoort(builder: MeshBuilder, styles: FortressStyles): void {
  const frame: Frame = {
    origin: layoutToWorld(JAN_ROODENPOORT_FRAME.origin, 0),
    yaw: JAN_ROODENPOORT_FRAME.heading,
  };
  const { u, v, eave } = JAN_ROODEN_TOWER;
  const body = {
    u,
    v,
    foot: JAN_ROODEN_FOOT,
    top: eave,
    passage: JAN_ROODEN_PASSAGE,
    openTop: true,
  };
  archedBlock(builder, styles.wall, frame, body);
  crown(builder, styles, frame);
  windows(builder, styles.dark, frame);
}

function toLayout(piece: PolygonWithHoles): PolygonWithHoles {
  return {
    outer: piece.outer.map(janRoodenToLayout),
    holes: piece.holes.map((h) => h.map(janRoodenToLayout)),
  };
}

/** The tower's plan, its passage included: no lot reaches into it. */
export function janRoodenFootprint(): PolygonWithHoles {
  return toLayout(fromRing(rectRing(JAN_ROODEN_TOWER.u, JAN_ROODEN_TOWER.v)));
}

/** The masonry less the passage (PLAN, Obstacles): one piece either side of it. */
export function janRoodenObstacles(): PolygonWithHoles[] {
  const { u, v } = JAN_ROODEN_TOWER;
  const way = rectRing([u[0] - 0.1, u[1] + 0.1], JAN_ROODEN_PASSAGE.v);
  return difference([fromRing(rectRing(u, v))], [fromRing(way)]).map(toLayout);
}

export function janRoodenPassage(): GatePassage {
  const { u } = JAN_ROODEN_TOWER;
  return {
    from: janRoodenToLayout([u[0], 0]),
    to: janRoodenToLayout([u[1], 0]),
    halfWidth: (JAN_ROODEN_PASSAGE.v[1] - JAN_ROODEN_PASSAGE.v[0]) / 2,
  };
}

/** The town walls stop where they met the layout's box of the gate: the tower is that box. */
export function janRoodenBlock(): GateBlock {
  const { u, v } = JAN_ROODEN_TOWER;
  return {
    centre: JAN_ROODENPOORT_FRAME.origin,
    heading: JAN_ROODENPOORT_FRAME.heading,
    half: [(u[1] - u[0]) / 2, (v[1] - v[0]) / 2],
  };
}

export const JAN_ROODENPOORT: BespokeGate = {
  anchor: JAN_ROODENPOORT_FRAME.origin,
  build: buildJanRoodenpoort,
  footprint: janRoodenFootprint,
  obstacles: janRoodenObstacles,
  passage: janRoodenPassage,
  block: janRoodenBlock,
};
